import Title_ru_RU              from '@/core/misc/decorators/Title_ru_RU';
import Title_ru_RU_Class        from '@/core/misc/decorators/Title_ru_RU_Class';

import TYPES                    from '@/core/services/Changelogs/types';

import BaseEntity               from "@/core/misc/BaseEntity";
import ShipmentToBuyerPosition  from '@/domains/ShipmentsToBuyer/ShipmentToBuyerPosition';
import Core                     from '@/core/Core';
import DateSimple               from '@/core/types/DateSimple';
import Partner                  from '@/domains/Partners/Partner';
import Company                  from '@/domains/Companies/Company';
import Sum                      from "@/core/types/Sum";
import Multiplier               from "@/core/types/Multiplier";
import Currency                 from "@/domains/Currencies/Currency";
import CurrencyRate             from "@/core/types/CurrencyRate";
import CurrenciesService        from "@/domains/Currencies/CurrenciesService";
import Shipment                 from "@/domains/Shipments/Shipment";
/**
 * Реализация покупателю.
 * Read-only: документ генерируется на беке из отгрузки поставщика
 * (одна реализация на одну отгрузку) и выгружается в 1С
 * как «Реализация товаров и услуг» + «Счёт-фактура выданный».
 */
@Title_ru_RU_Class('Реализация покупателю')
class ShipmentToBuyer extends BaseEntity {

  @Title_ru_RU('Продавец', TYPES.OBJECT)
  public seller: Partner;

  @Title_ru_RU('Юрлицо продавца', TYPES.OBJECT)
  public sellerCompany: Company;

  @Title_ru_RU('Покупатель', TYPES.OBJECT)
  public buyer: Partner;

  @Title_ru_RU('Юрлицо покупателя', TYPES.OBJECT)
  public buyerCompany: Company;

  @Title_ru_RU('Дата отгрузки', TYPES.DATE)
  public shipmentDate: DateSimple;

  // ---- Валюта сделки ----

  private _orderCurrency: Currency | null;
  private _orderCurrencyCode: string | null;

  // ---- Валюта учёта ----

  private _accountingCurrency: Currency | null;
  private _accountingCurrencyCode: string | null;

  private _accountingCurrencyRate: CurrencyRate;

  public positions: ShipmentToBuyerPosition[] = [];

  /** Отгрузка от поставщика — основание. С бека приходит только id, accountingSumFull,
   * documentDate, documentType, documentNumber
   */
  public shipmentFromSeller: Shipment | {
    id: number;
    accountingSumFull: Sum | null;
    documentDate: DateSimple | null;
    documentType: { id: string; name: string } | null;
    documentNumber: string;
    isVerified: boolean;
  };

  // TODO: статусы выгрузки в 1С (onesUid, vatInvoiceOnesUid, exportedToOnesAt)
  //  на беке приватные/отсутствуют и не сериализуются — ждём доработку модели.

  /* ────────────────────────────────────────
     Вычисляемые суммы документа
     (на беке не хранятся — только по позициям)
     ──────────────────────────────────────── */

  @Title_ru_RU('Полная сумма', TYPES.SUM)
  get sumFull(): Sum {
    return this.positions.reduce(
      (acc: Sum, position: ShipmentToBuyerPosition) => acc.plus(position.sumFull),
      Sum.fromServer('0'),
    );
  }

  @Title_ru_RU('Сумма НДС', TYPES.SUM)
  get vatSum(): Sum {
    return this.positions.reduce(
      (acc: Sum, position: ShipmentToBuyerPosition) => acc.plus(position.vatSum),
      Sum.fromServer('0'),
    );
  }

  @Title_ru_RU('Полная сумма в валюте учёта', TYPES.SUM)
  get accountingSumFull(): Sum {
    return this.positions.reduce(
      (acc: Sum, position: ShipmentToBuyerPosition) => acc.plus(position.accountingSumFull),
      Sum.fromServer('0'),
    );
  }
  @Title_ru_RU('Сумма НДС в валюте учета', TYPES.SUM)
  get accountingVatSum(): Sum {
    return this.positions.reduce(
      (acc: Sum, position: ShipmentToBuyerPosition) => acc.plus(position.accountingVatSum),
      Sum.fromServer('0'),
    );
  }
  @Title_ru_RU('Тех наценка', TYPES.NUMBER)
  get sftPriceMod(): Multiplier {
    return this.positions[0]?.sftPriceMod ?? Multiplier.fromServer('1');
  }

  /**
   * Фактическая ставка НДС документа, выведенная из сумм: полная сумма,
   * делённая на сумму без НДС, даёт множитель (1.20 = 20%).
   *
   * Не VatRate: у того SCALE = 0, и 19.63% округлились бы до 20, спрятав
   * ровно то расхождение, ради которого величина считается. Multiplier
   * держит два знака процента.
   *
   * Смысл — контрольный: если позиции документа несут разные ставки или
   * ставка не та, что заявлена, здесь появится нецелое значение. Позиционную
   * проверку не заменяет — validatePositionsVatRate укажет конкретную позицию.
   *
   * Нулевая сумма без НДС (весь документ по нулевой ставке) даёт нейтральный
   * множитель: делить не на что, а ноль процентов — правдивый ответ.
   */
  @Title_ru_RU('Ставка НДС фактическая', TYPES.NUMBER)
  get vatRateActual(): Multiplier {
    const sumWithoutVat = this.sumFull.minus(this.vatSum);

    if (sumWithoutVat.equals(Sum.fromServer('0'))) {
      return Multiplier.fromPercent(0);
    }

    return this.sumFull.divBySum(sumWithoutVat);
  }

  @Title_ru_RU('Наценка полная', TYPES.NUMBER)
  get priceModActual(): Multiplier {
    if (!this.shipmentFromSeller.accountingSumFull) {
      return Multiplier.fromServer('1');
    }

    return this.accountingSumFull.divBySum(this.shipmentFromSeller.accountingSumFull);
  }

  /* ────────────────────────────────────────
    Валюта сделки — код ведущий, объект из словаря
    ──────────────────────────────────────── */

  get orderCurrencyCode(): string | null { return this._orderCurrencyCode; }
  set orderCurrencyCode(value: string | null) {
    this._orderCurrencyCode = value;

    if (this._orderCurrencyCode !== null && this._orderCurrencyCode !== undefined) {
      this._orderCurrency = CurrenciesService.currencies.get(this._orderCurrencyCode) ?? null;
    } else {
      this._orderCurrency = null;
    }
  }

  get orderCurrency(): Currency | null { return this._orderCurrency; }
  set orderCurrency(value: Currency | null) {
    this.orderCurrencyCode = value?.code ?? null;
  }

  /* ────────────────────────────────────────
     Валюта учёта — код ведущий, объект из словаря
     ──────────────────────────────────────── */

  get accountingCurrencyCode(): string | null { return this._accountingCurrencyCode; }
  set accountingCurrencyCode(value: string | null) {
    this._accountingCurrencyCode = value;

    if (this._accountingCurrencyCode !== null && this._accountingCurrencyCode !== undefined) {
      this._accountingCurrency = CurrenciesService.currencies.get(this._accountingCurrencyCode) ?? null;
    } else {
      this._accountingCurrency = null;
    }
  }

  get accountingCurrency(): Currency | null { return this._accountingCurrency; }
  set accountingCurrency(value: Currency | null) {
    /** Ведущий — код: его сеттер подтянет объект из словаря. */
    this.accountingCurrencyCode = value?.code ?? null;
  }

  @Title_ru_RU('Курс валюты учёта', TYPES.NUMBER)
  get accountingCurrencyRate(): CurrencyRate { return this._accountingCurrencyRate; }
  set accountingCurrencyRate(value: CurrencyRate) { this._accountingCurrencyRate = value; }



  get sellerString(): string | null {
    if (!this.sellerCompany) {
      return 'Не выбран';
    }
    if (this.sellerCompany.name) {
      return `${this.sellerCompany.name} (${this.seller.name})`;
    }
    return this.seller.name;
  }

  get buyerString(): string | null {
    if (this.buyerCompany?.name) {
      return `${this.buyerCompany.name} ${this.buyer?.name}`;
    }
    if (this.buyer?.name) {
      return this.buyer.name;
    }
    return '--';
  }


  /* ────────────────────────────────────────
     fromServer
     ──────────────────────────────────────── */

  static fromServer(data: any, shipment: ShipmentToBuyer = new ShipmentToBuyer()): ShipmentToBuyer {
    super.fromServer(data, shipment);

    shipment.seller        = Core.utils.hydrate(data.seller, Partner.fromServer)!;
    shipment.sellerCompany = Core.utils.hydrate(data.sellerCompany, Company.fromServer)!;
    shipment.buyer         = Core.utils.hydrate(data.buyer, Partner.fromServer)!;
    shipment.buyerCompany  = Core.utils.hydrate(data.buyerCompany, Company.fromServer)!;

    shipment.shipmentDate = DateSimple.fromServer(data.shipmentDate)!;
    // ---- Валюта сделки ----
    shipment.orderCurrencyCode = data.orderCurrencyCode;

    // ---- Валюта учёта ----
    shipment.accountingCurrencyCode = data.accountingCurrencyCode;
    shipment.accountingCurrencyRate = Core.utils.hydrate(data.accountingCurrencyRate, CurrencyRate.fromServer)!;

    shipment.positions = Core.utils.hydrateArray(data.positions, ShipmentToBuyerPosition.fromServer);

    shipment.shipmentFromSeller = Core.utils.hydrate(data.shipmentFromSeller, Shipment.fromServer);

    /** Обратная ссылка — последним шагом, когда документ уже гидрирован.
     * Сеттер чистый, каскада не вызывает: значения позиций пришли с бэка
     * и пересчитывать их фронтом не нужно. */
    shipment.positions.forEach((position: ShipmentToBuyerPosition) => position.shipmentToBuyer = shipment);

    return shipment;
  }

}

export default ShipmentToBuyer;
